import { AtonTargetInfo, extractAtonTargetInfos } from './atonTargetInfo';
import {
  BaseStationTargetInfo,
  extractBasestationTargetInfos,
} from './baseStationTargetInfo';
import { extractShipInfos, ShipInfo } from './shipInfo';
import {
  extractVirtualAtonTargetInfos,
  VirtualAtonTargetInfo,
} from './virtualAtonTargetInfo';
import { extractMethydTargetInfos, MethydTargetInfo } from './methydTargetInfo';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import {
  EnvironmentalTargetInfo,
  extractEnvironmentalTargetInfos,
} from './environmentalTargetInfo';
import { DasTargetInfo, extractDasTargetInfos } from './dasTargetInfo';
import { extractRadarTargetInfos, RadarTargetInfo } from './radarTargetInfo';
import { DroneTargetInfo, extractDroneTargetInfos } from './droneTargetInfo';

interface TargetInfo {
  lat: number;
  lon: number;
  type: string;
  mmsi: string;
  values: {
    id: string;
    value: string;
    name: string;
  }[];
  values_json: { [key: string]: any };
}

export interface ExtractedTargetInfo {
  extractedTargetInfo: TargetInfoType[];
  extractedTargetInfoType: TargetTypeEnum;
}

export type TargetInfoType =
  | BaseStationTargetInfo
  | AtonTargetInfo
  | ShipInfo
  | VirtualAtonTargetInfo
  | MethydTargetInfo
  | VesselResult
  | EnvironmentalTargetInfo
  | DasTargetInfo
  | RadarTargetInfo
  | DroneTargetInfo;

export enum TargetTypeEnum {
  Ship = 'ship',
  BaseStation = 'basestation',
  Aton = 'aton',
  VirtualAton = 'virtual aton',
  Methyd = 'methyd',
  ExtendedVessel = 'extended vessel',
  Environmental = 'environmental',
  Das = 'das',
  Radar = 'radar',
  Drone = 'drone',
}

export interface QueryPointResult {
  targetInfo: TargetInfo[];
}

export function getTargetInfoName(
  targetInfo: TargetInfoType,
  targetType: TargetTypeEnum,
) {
  // Guard against undefined or null targetInfo
  if (!targetInfo) {
    return 'Unknown';
  }

  if ('name' in targetInfo) {
    return targetInfo.name;
  }
  if (targetType === TargetTypeEnum.BaseStation) {
    const baseStationInfo = targetInfo as BaseStationTargetInfo;
    return baseStationInfo.mmsi || 'Basestation';
  } else if (targetType === TargetTypeEnum.Methyd) {
    return 'MetHyd';
  } else if (targetType === TargetTypeEnum.Environmental) {
    return 'Environmental';
  } else if (targetType === TargetTypeEnum.ExtendedVessel) {
    return (targetInfo as VesselResult)?.basic?.vesselName
      ? (targetInfo as VesselResult)?.basic?.vesselName
      : 'Unknown';
  } else if (targetType === TargetTypeEnum.Das) {
    const dasInfo = targetInfo as DasTargetInfo;
    const site = dasInfo.site || 'Unknown Site';
    const trackId = dasInfo.trackId || 'Unknown Track';
    return `${site} ${trackId}`;
  } else if (targetType === TargetTypeEnum.Radar) {
    return 'Radar Target';
  } else if (targetType === TargetTypeEnum.Drone) {
    return 'Drone';
  }
  return 'Unknown';
}

export function extractTargetInfos(
  result: QueryPointResult,
): ExtractedTargetInfo {
  const tmpExtractedTargetInfo: ExtractedTargetInfo = {
    extractedTargetInfo: [],
    extractedTargetInfoType: TargetTypeEnum.Ship,
  };

  const lowerCaseType = result?.targetInfo?.[0]?.type.toLowerCase();

  if (lowerCaseType === 'basestation') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.BaseStation;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractBasestationTargetInfos(result);
  } else if (lowerCaseType === 'aton') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Aton;
    tmpExtractedTargetInfo.extractedTargetInfo = extractAtonTargetInfos(result);
  } else if (lowerCaseType === 'virtual aton') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.VirtualAton;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractVirtualAtonTargetInfos(result);
  } else if (lowerCaseType === 'methyd') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Methyd;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractMethydTargetInfos(result);
  } else if (lowerCaseType === 'environmental') {
    tmpExtractedTargetInfo.extractedTargetInfoType =
      TargetTypeEnum.Environmental;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractEnvironmentalTargetInfos(result);
  } else if (lowerCaseType === 'das') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Das;
    tmpExtractedTargetInfo.extractedTargetInfo = extractDasTargetInfos(result);
  } else if (lowerCaseType === 'radar target') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Radar;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractRadarTargetInfos(result);
  } else if (lowerCaseType === 'drone') {
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Drone;
    tmpExtractedTargetInfo.extractedTargetInfo =
      extractDroneTargetInfos(result);
  } else {
    // Default case: extract ship information
    tmpExtractedTargetInfo.extractedTargetInfoType = TargetTypeEnum.Ship;
    tmpExtractedTargetInfo.extractedTargetInfo = extractShipInfos(result);
  }

  return tmpExtractedTargetInfo;
}
